import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Globe, Pencil, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface BrandKitData {
  business_name: string;
  website_url: string;
  logo_url: string | null;
  colors: {
    primary: string | null;
    secondary: string | null;
    tertiary: string | null;
  };
  brand_voice: string | null;
  description: string | null;
  value_proposition: string | null;
  target_audience: string | null;
  industry: string | null;
  key_services: string | null;
  brand_personality: string | null;
  media_library: Array<{
    id: string;
    url: string;
    name: string;
    type: 'logo' | 'brain' | 'generated' | 'website';
  }>;
}

interface BrandKitModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BrandKitModal({ isOpen, onClose }: BrandKitModalProps) {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const [brandKit, setBrandKit] = useState<BrandKitData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Edit states
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedBusinessName, setEditedBusinessName] = useState('');
  const [editedWebsiteUrl, setEditedWebsiteUrl] = useState('');
  const [editedLogoUrl, setEditedLogoUrl] = useState('');
  const [editedColors, setEditedColors] = useState({ primary: '', secondary: '', tertiary: '' });
  const [editedBrandVoice, setEditedBrandVoice] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedValueProp, setEditedValueProp] = useState('');
  const [editedTargetAudience, setEditedTargetAudience] = useState('');
  const [editedIndustry, setEditedIndustry] = useState('');
  const [editedKeyServices, setEditedKeyServices] = useState('');
  const [editedBrandPersonality, setEditedBrandPersonality] = useState('');

  // Fetch brand kit data
  const fetchBrandKit = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/brand-kit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: BrandKitData };
      if (data.success && data.data) {
        setBrandKit(data.data);
        // Initialize edit states
        setEditedBusinessName(data.data.business_name || '');
        setEditedWebsiteUrl(data.data.website_url || '');
        setEditedLogoUrl(data.data.logo_url || '');
        setEditedColors({
          primary: data.data.colors?.primary || '',
          secondary: data.data.colors?.secondary || '',
          tertiary: data.data.colors?.tertiary || '',
        });
        setEditedBrandVoice(data.data.brand_voice || '');
        setEditedDescription(data.data.description || '');
        setEditedValueProp(data.data.value_proposition || '');
        setEditedTargetAudience(data.data.target_audience || '');
        setEditedIndustry(data.data.industry || '');
        setEditedKeyServices(data.data.key_services || '');
        setEditedBrandPersonality(data.data.brand_personality || '');
      }
    } catch (error) {
      console.error('Failed to fetch brand kit:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchBrandKit();
    }
  }, [isOpen, fetchBrandKit]);

  // Save field
  const saveField = async (field: string, value: unknown) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/brand-kit`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });
      const data = await response.json() as { success: boolean };
      if (data.success) {
        // Refresh brand kit
        await fetchBrandKit();
        setEditingField(null);
      }
    } catch (error) {
      console.error('Failed to save field:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Render editable field
  const renderEditableField = (
    label: string,
    description: string,
    fieldKey: string,
    value: string,
    editValue: string,
    setEditValue: (v: string) => void,
    multiline: boolean = false
  ) => (
    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{label}</h4>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{description}</p>
        </div>
        {editingField !== fieldKey && (
          <button
            onClick={() => setEditingField(fieldKey)}
            className={`p-1.5 rounded-lg transition-colors ${
              isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-200 text-slate-500'
            }`}
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
      </div>
      {editingField === fieldKey ? (
        <div className="space-y-2">
          {multiline ? (
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className={`w-full p-3 rounded-lg text-sm resize-none ${
                isDark
                  ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
              } border focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              rows={4}
              placeholder={`Enter ${label.toLowerCase()}...`}
            />
          ) : (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className={`w-full p-3 rounded-lg text-sm ${
                isDark
                  ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
              } border focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              placeholder={`Enter ${label.toLowerCase()}...`}
            />
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setEditingField(null)}
              className={`px-3 py-1.5 text-sm rounded-lg ${
                isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={() => saveField(fieldKey, editValue)}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm rounded-lg bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1"
            >
              {isSaving && <Loader2 className="w-3 h-3 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      ) : (
        <p className={`text-sm ${value ? (isDark ? 'text-gray-300' : 'text-slate-700') : (isDark ? 'text-gray-500' : 'text-slate-400')}`}>
          {value || `No ${label.toLowerCase()} set`}
          {value && value.length > 200 && '...'}
        </p>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className={`w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden ${
                isDark ? 'bg-dark-800' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Brand Kit
                </h2>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-4rem)] p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Business Info Row */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Business Name */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                        <div className="flex items-start justify-between mb-3">
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Your business</h4>
                          {editingField !== 'business' && (
                            <button
                              onClick={() => setEditingField('business')}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-200 text-slate-500'
                              }`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {editingField === 'business' ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={editedBusinessName}
                              onChange={(e) => setEditedBusinessName(e.target.value)}
                              className={`w-full p-2 rounded-lg text-sm ${
                                isDark
                                  ? 'bg-dark-700 border-white/10 text-white'
                                  : 'bg-white border-slate-200 text-slate-900'
                              } border`}
                              placeholder="Business name"
                            />
                            <div className="flex items-center gap-2">
                              <Globe className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                              <input
                                type="text"
                                value={editedWebsiteUrl}
                                onChange={(e) => setEditedWebsiteUrl(e.target.value)}
                                className={`flex-1 p-2 rounded-lg text-sm ${
                                  isDark
                                    ? 'bg-dark-700 border-white/10 text-white'
                                    : 'bg-white border-slate-200 text-slate-900'
                                } border`}
                                placeholder="https://example.com"
                              />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setEditingField(null)}
                                className={`px-3 py-1.5 text-sm rounded-lg ${
                                  isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={async () => {
                                  setIsSaving(true);
                                  await saveField('business_name', editedBusinessName);
                                  await saveField('website_url', editedWebsiteUrl);
                                  setIsSaving(false);
                                  setEditingField(null);
                                }}
                                disabled={isSaving}
                                className="px-3 py-1.5 text-sm rounded-lg bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1"
                              >
                                {isSaving && <Loader2 className="w-3 h-3 animate-spin" />}
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {brandKit?.business_name || 'Your Business'}
                            </h3>
                            {brandKit?.website_url && (
                              <div className="flex items-center justify-center gap-1 mt-2">
                                <Globe className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                                <a
                                  href={brandKit.website_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`text-sm ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                                >
                                  {brandKit.website_url}
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Logo */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Logo</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                              Keeps your posts visually consistent.
                            </p>
                          </div>
                          {editingField !== 'logo' && (
                            <button
                              onClick={() => setEditingField('logo')}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-200 text-slate-500'
                              }`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {editingField === 'logo' ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editedLogoUrl}
                              onChange={(e) => setEditedLogoUrl(e.target.value)}
                              className={`w-full p-2 rounded-lg text-sm ${
                                isDark
                                  ? 'bg-dark-700 border-white/10 text-white'
                                  : 'bg-white border-slate-200 text-slate-900'
                              } border`}
                              placeholder="Logo URL"
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setEditingField(null)}
                                className={`px-3 py-1.5 text-sm rounded-lg ${
                                  isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => saveField('logo_url', editedLogoUrl)}
                                disabled={isSaving}
                                className="px-3 py-1.5 text-sm rounded-lg bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-24 rounded-lg overflow-hidden">
                            {brandKit?.logo_url ? (
                              <img
                                src={brandKit.logo_url}
                                alt="Logo"
                                className="max-h-full max-w-full object-contain"
                              />
                            ) : (
                              <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                No logo set
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Color Palette Row */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Colors */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Color palette</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                              Used in your post visuals.
                            </p>
                          </div>
                          {editingField !== 'colors' && (
                            <button
                              onClick={() => setEditingField('colors')}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-200 text-slate-500'
                              }`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {editingField === 'colors' ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-2">
                              {['primary', 'secondary', 'tertiary'].map((colorKey) => (
                                <div key={colorKey}>
                                  <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'} capitalize mb-1 block`}>
                                    {colorKey}
                                  </label>
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="color"
                                      value={editedColors[colorKey as keyof typeof editedColors] || '#666666'}
                                      onChange={(e) => setEditedColors(prev => ({ ...prev, [colorKey]: e.target.value }))}
                                      className="w-8 h-8 rounded cursor-pointer"
                                    />
                                    <input
                                      type="text"
                                      value={editedColors[colorKey as keyof typeof editedColors]}
                                      onChange={(e) => setEditedColors(prev => ({ ...prev, [colorKey]: e.target.value }))}
                                      className={`flex-1 p-1 text-xs rounded ${
                                        isDark
                                          ? 'bg-dark-700 border-white/10 text-white'
                                          : 'bg-white border-slate-200 text-slate-900'
                                      } border`}
                                      placeholder="#000000"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setEditingField(null)}
                                className={`px-3 py-1.5 text-sm rounded-lg ${
                                  isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => saveField('colors', editedColors)}
                                disabled={isSaving}
                                className="px-3 py-1.5 text-sm rounded-lg bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 mt-3">
                            {[
                              { key: 'primary', label: 'Primary' },
                              { key: 'secondary', label: 'Secondary' },
                              { key: 'tertiary', label: 'Tertiary' },
                            ].map(({ key, label }) => {
                              const color = brandKit?.colors?.[key as keyof typeof brandKit.colors];
                              return (
                                <div key={key} className="flex flex-col items-center">
                                  <div
                                    className="w-14 h-14 rounded-full shadow-md border-2 border-white/20"
                                    style={{ backgroundColor: color || '#374151' }}
                                  />
                                  <span className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                    {label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Brand Voice */}
                      <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Brand voice</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                              Sets your post&apos;s tone.
                            </p>
                          </div>
                          {editingField !== 'brand_voice' && (
                            <button
                              onClick={() => setEditingField('brand_voice')}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-200 text-slate-500'
                              }`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {editingField === 'brand_voice' ? (
                          <div className="space-y-2">
                            <textarea
                              value={editedBrandVoice}
                              onChange={(e) => setEditedBrandVoice(e.target.value)}
                              className={`w-full p-2 rounded-lg text-sm resize-none ${
                                isDark
                                  ? 'bg-dark-700 border-white/10 text-white'
                                  : 'bg-white border-slate-200 text-slate-900'
                              } border`}
                              rows={4}
                              placeholder="Describe your brand's tone and voice..."
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setEditingField(null)}
                                className={`px-3 py-1.5 text-sm rounded-lg ${
                                  isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => saveField('brand_voice', editedBrandVoice)}
                                disabled={isSaving}
                                className="px-3 py-1.5 text-sm rounded-lg bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className={`text-sm line-clamp-4 ${
                            brandKit?.brand_voice
                              ? (isDark ? 'text-gray-300' : 'text-slate-700')
                              : (isDark ? 'text-gray-500' : 'text-slate-400')
                          }`}>
                            {brandKit?.brand_voice || 'No brand voice set'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Description and Value Proposition Row */}
                    <div className="grid grid-cols-2 gap-4">
                      {renderEditableField(
                        'Description',
                        'What your business does.',
                        'description',
                        brandKit?.description || '',
                        editedDescription,
                        setEditedDescription,
                        true
                      )}

                      {renderEditableField(
                        'Value proposition',
                        'What makes your business unique.',
                        'value_proposition',
                        brandKit?.value_proposition || '',
                        editedValueProp,
                        setEditedValueProp,
                        true
                      )}
                    </div>

                    {/* Target Audience and Industry Row */}
                    <div className="grid grid-cols-2 gap-4">
                      {renderEditableField(
                        'Target audience',
                        'Who your ideal customers are.',
                        'target_audience',
                        brandKit?.target_audience || '',
                        editedTargetAudience,
                        setEditedTargetAudience,
                        true
                      )}

                      {renderEditableField(
                        'Industry',
                        'Your business sector or niche.',
                        'industry',
                        brandKit?.industry || '',
                        editedIndustry,
                        setEditedIndustry,
                        false
                      )}
                    </div>

                    {/* Key Services and Brand Personality Row */}
                    <div className="grid grid-cols-2 gap-4">
                      {renderEditableField(
                        'Key services/products',
                        'Main offerings of your business.',
                        'key_services',
                        brandKit?.key_services || '',
                        editedKeyServices,
                        setEditedKeyServices,
                        true
                      )}

                      {renderEditableField(
                        'Brand personality',
                        'Your brand\'s character traits.',
                        'brand_personality',
                        brandKit?.brand_personality || '',
                        editedBrandPersonality,
                        setEditedBrandPersonality,
                        true
                      )}
                    </div>

                    {/* Media Library */}
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Media library</h4>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            Used for your post visuals.
                          </p>
                        </div>
                      </div>
                      {brandKit?.media_library && brandKit.media_library.length > 0 ? (
                        <div className="grid grid-cols-4 gap-3">
                          {brandKit.media_library.map((item) => (
                            <div
                              key={item.id}
                              className={`aspect-square rounded-lg overflow-hidden ${
                                isDark ? 'bg-dark-700' : 'bg-white'
                              } border ${isDark ? 'border-white/10' : 'border-slate-200'}`}
                            >
                              <img
                                src={item.url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                          No media in library. Upload images in Brain AI to add them here.
                        </div>
                      )}
                    </div>

                    {/* Brain AI Link */}
                    <div className={`p-4 rounded-xl border-2 border-dashed ${isDark ? 'border-purple-500/30 bg-purple-500/5' : 'border-purple-200 bg-purple-50'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Want to add more brand data?
                          </h4>
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            Add your website URL in Brain AI to automatically extract brand information.
                          </p>
                        </div>
                        <a
                          href="/dashboard/brain"
                          className="px-4 py-2 text-sm font-medium rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          Go to Brain AI
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
